"use client";

import { useSectionNavTheme } from "@/app/hooks/useSectionNavTheme";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { Heading3, Paragraph } from "../typography";
import { AustraliaMapMaskedCard } from "../australia-map-masked-card";

export const TheChallengeSolution = () => {
  useSectionNavTheme({
    trigger: "#vmap-challenge-solution",
    isDark: true,
  });

  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add("(min-width: 768px)", () => {
      gsap.set([".australia-card-2", ".australia-card-3"], {
        opacity: 0,
      });

      gsap.from(".challenge-title", {
        opacity: 0,
        y: 40,
        duration: 0.8,
        ease: "power2.out",
        scrollTrigger: {
          trigger: "#vmap-challenge-solution",
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      });

      const pinnedTl = gsap.timeline({
        ease: "none",
        scrollTrigger: {
          trigger: "#vmap-challenge-solution",
          start: "top top",
          end: "bottom 95%",
          pin: ".map-cards",
          scrub: 1,
          invalidateOnRefresh: true,
        },
      });

      pinnedTl
        .to(".australia-card-1", {
          opacity: 0,
        })
        .to(
          ".australia-card-1 .map-card__content",
          {
            y: -300,
          },
          "<"
        )
        .to(
          ".australia-card-2",
          {
            opacity: 1,
          },
          "<"
        )
        .to(".australia-card-2 .map-card__content", {
          y: -300,
        })
        .to([".australia-card-3"], {
          opacity: 1,
        });
    });
  });

  return (
    <section
      id="vmap-challenge-solution"
      className="relative py-20 lg:py-32 xl:py-40"
    >
      <div className="absolute inset-0 dark:bg-gradient-to-l from-haizea-green/50 via-haizea-dark to-slate-700/60"></div>
      <div className="container">
        <div className="flex items-start flex-col lg:flex-row gap-10 lg:gap-20">
          <div className="map-cards relative rounded-2xl border border-slate-200 dark:border-slate-500 w-full lg:w-[600px] lg:max-w-1/2 max-h-[60vh] aspect-square shrink-0 bg-slate-50/10 backdrop-blur-sm">
            <AustraliaMapMaskedCard
              title="EUDR"
              subtitle="EU Deforestation Regulation — supply chains now demand continental evidence"
              className="australia-card-1 w-full h-full bg-linear-to-r from-haizea-blue via-90% via-haizea-green to-haizea-orange"
            />
            <AustraliaMapMaskedCard
              title="Sentinel-2 + LiDAR"
              subtitle="AI trained on airborne truth, applied continent-wide"
              className="australia-card-2 w-full h-full bg-linear-to-r from-haizea-green via-90% via-haizea-green to-haizea-orange"
            />
            <AustraliaMapMaskedCard
              title="VMAP"
              subtitle="Powered by Terrak.io, built by ANU and Haizea Analytics"
              className="australia-card-3 w-full h-full bg-radial from-haizea-green via-haizea-blue to-haizea-orange"
            />
          </div>
          <div className="relative w-full">
            <ChallengeContent />
            <SolutionContent />
          </div>
        </div>
      </div>
    </section>
  );
};

const ChallengeContent = () => {
  return (
    <div className="space-y-6 text-lg leading-relaxed dark:text-slate-300 bg-white/5 lg:bg-transparent backdrop-blur-sm lg:backdrop-blur-none p-6 lg:p-0">
      <Heading3 className="challenge-title text-slate-800 dark:text-slate-200">
        The Challenge
      </Heading3>
      <div className="challenge-content space-y-4 lg:space-y-10">
        <Paragraph>
          Monitoring forests underpins a growing list of policy and commercial 
          decisions — national greenhouse accounting, biodiversity conservation, 
          land-use planning, and increasingly, deforestation-free supply chains.
          For example, the EU Deforestation Regulation now requires Australian 
          exporters to prove their supply chains are not linked to recent 
          deforestation. Evidence must be produced, defended and refreshed for 
          every relevant property across the entirety of Australia — a landmass
          nearly twice the size of the EU — and it must hold up to scrutiny 
          from regulators, auditors and trade partners.
        </Paragraph>

        <Paragraph>
          Remote sensing is the only viable source at that scale, but using 
          it well is not easy: high-resolution, up-to-date, cloud-free, radiometrically 
          consistent, analysis-ready observations have to be assembled from a continuous 
          stream of raw scenes. The resulting archives reach tens of petabytes, so any 
          operational product must also solve the engineering problem of storing, 
          processing and repeatedly rerunning that archive at national scale.
        </Paragraph>
      </div>
    </div>
  );
};

const SolutionContent = () => {
  const solutionFeatures = [
    {
      title: "Analysis-Ready Sentinel-2 on Terrak.io",
      description:
        "Terrak.io hosts the full Sentinel-2 archive as a single queryable resource. Users describe the analysis they want, not the plumbing: no tile downloads, no grid alignment, no cloud masking.",
    },
    {
      title: "Airborne LiDAR as Ground Truth",
      description:
        "Publicly available airborne LiDAR surveys give centimetre-scale vertical structure for slices of the continent. Those surveys are used to label what forest actually looks like from space — a training signal far richer than photo-interpreted polygons.",
    },
    {
      title: "AI Trained and Scaled on One Engine",
      description:
        "AI models are trained and optimised directly against Terrak.io's training-sample and batch pipelines, then run annually at national scale. The same engine serves the model at an interactive tile, a national GeoTIFF or a per-property zonal statistic.",
    },
  ];

  return (
    <div className="mt-10 lg:mt-40 space-y-6 text-lg leading-relaxed dark:text-slate-300 bg-white/5 lg:bg-transparent backdrop-blur-sm lg:backdrop-blur-none p-6 lg:p-0">
      <Heading3 className="text-slate-800 dark:text-slate-200">
        The Solution
      </Heading3>
      <div className="solution-content space-y-4 lg:space-y-10">
        <Paragraph>
          The Australian National University and Haizea Analytics developed
          VMAP — the Vegetation Monitoring and Assessment Program — to
          meet that challenge. It is an accurate, annually updated,
          high-quality product that maps{" "}
          <span className="font-semibold text-slate-800 dark:text-slate-200">
            forest extent and forest loss across the whole of Australia
            since 2020
          </span>
          . The platform also includes a tree crown cover dataset extending
          back to 2017, and all data is{" "}
          <span className="font-semibold text-slate-800 dark:text-slate-200">
            freely accessible for non-commercial use
          </span>
          .
        </Paragraph>

        {solutionFeatures.map((feature, index) => (
          <div key={index} className="space-y-2">
            <h4 className="text-xl font-semibold text-haizea-green">
              {feature.title}
            </h4>
            <Paragraph>{feature.description}</Paragraph>
          </div>
        ))}

        <Paragraph>
          The result is a suite of vegetation products with{" "}
          <span className="font-semibold text-slate-800 dark:text-slate-200">
            continental coverage, annual updates and a reproducible,
            LiDAR-grounded methodology
          </span>{" "}
          — produced on the same platform that serves it, so every layer
          is traceable back to the pixels and the model that generated it.
        </Paragraph>
      </div>
    </div>
  );
};
